import { endSession as endBrowserSession, hasSession as hasBrowserSession } from "@serophin/browser";
import { clearClientCache } from "@serophin/nottldr-shared";
import { useEffect, useState } from "preact/hooks";

/** The edge Worker reads this at `/` only, to choose the landing page or the feed shell. It holds no token. */
export const SIGNED_IN_COOKIE = "nottldr_signed_in";

const COOKIE_MAX_AGE_SECONDS = 365 * 24 * 60 * 60;

/** `loading` is the state of a hydrating page before its first check; it renders as signed out, like the prerender. */
export type SessionStatus = "loading" | "signedIn" | "signedOut";

type Listener = (status: SessionStatus) => void;

const listeners = new Set<Listener>();

let status: SessionStatus = "signedOut";

/** A session exists while the browser holds an access token or can renew one (its refresh token is in an HttpOnly cookie). */
export const hasSession = (): boolean => {
  try {
    return hasBrowserSession();
  } catch {
    return false;
  }
};

const writeHintCookie = (signedIn: boolean): void => {
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const maxAge = signedIn ? COOKIE_MAX_AGE_SECONDS : 0;
  document.cookie = `${SIGNED_IN_COOKIE}=${signedIn ? "1" : ""}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
};

const setStatus = (next: SessionStatus): void => {
  if (next === status) {
    return;
  }
  status = next;
  for (const listener of [...listeners]) {
    listener(next);
  }
};

/** Reads the browser's tokens, keeps the hint cookie in step with them and tells everyone who follows the session. */
export const refreshSession = (): void => {
  const signedIn = hasSession();
  writeHintCookie(signedIn);
  setStatus(signedIn ? "signedIn" : "signedOut");
};

const readStatus = (): SessionStatus => (hasSession() ? "signedIn" : "signedOut");

/**
 * Starts the session state before the first render. A hydrating page starts as `loading` (what the prerender
 * showed) and its first effect calls `refreshSession`; a fresh render knows the answer at once.
 */
export const initSession = (options: { hydrating: boolean }): void => {
  status = options.hydrating ? "loading" : readStatus();
  if (!options.hydrating) {
    writeHintCookie(status === "signedIn");
  }
  window.addEventListener("storage", refreshSession);
  window.addEventListener("pageshow", refreshSession);
};

export const useSessionStatus = (): SessionStatus => {
  const [current, setCurrent] = useState<SessionStatus>(status);
  useEffect(() => {
    listeners.add(setCurrent);
    setCurrent(status);
    return () => {
      listeners.delete(setCurrent);
    };
  }, []);
  return current;
};

/** True only for a settled signed-in session; `loading` counts as signed out so the first paint matches the prerender. */
export const useIsSignedIn = (): boolean => useSessionStatus() === "signedIn";

/** Signs out on the server (best effort: it revokes the refresh token and clears its cookie), drops the cached data and the tokens, and clears the hint cookie. */
export const endSession = async (): Promise<void> => {
  await endBrowserSession();
  clearClientCache();
  refreshSession();
};
